import { omit, uniq } from 'es-toolkit';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';

import {
  ComponentDesignLayoutModel,
  ComponentDesignSummariesAndTimestamps,
  Insertion,
  LayoutModel,
  ModelCategory,
  ProjectSynchronizationPayload,
} from '@paragon/contract-library';
import { LockTarget } from '@paragon/data-connections';
import { omitBy } from '@paragon/utilities';

export type LayoutModelsState = { [guid: string]: LayoutModel };
const initialState: LayoutModelsState = {};

const layoutModesSlice = createSlice({
  name: 'layoutModels',
  initialState,
  reducers: {
    nonSynchronizedLayoutModelLoaded: (state, action: PayloadAction<LayoutModel | LayoutModel[]>) => {
      const layoutModels = Array.isArray(action.payload) ? action.payload : [action.payload];
      for (const model of layoutModels) {
        state[model.guid] = model;
      }
    },
    projectArchived: (state, action: PayloadAction<{ guid: string; descendants: string[] }>) =>
      omit(state, uniq([action.payload.guid].concat(action.payload.descendants))),
    componentDesignsBatchLocked: (
      state,
      action: PayloadAction<{
        updatedSummariesAndTimestamps: ComponentDesignSummariesAndTimestamps;
        lockTargets: readonly LockTarget[];
      }>,
    ) => {
      for (const summary of action.payload.updatedSummariesAndTimestamps.componentDesignSummaries) {
        if (summary.guid in state) {
          const summaryLayoutModel = state[summary.guid] as Draft<ComponentDesignLayoutModel>;

          summaryLayoutModel.analogLocked = summary.analogLocked;
          summaryLayoutModel.hangersLocked = summary.hangersLocked;
          summaryLayoutModel.loadingLocked = summary.loadingLocked;
          summaryLayoutModel.membersLocked = summary.membersLocked;
          summaryLayoutModel.platingLocked = summary.platingLocked;
        }
      }
    },
    componentDesignsDeletedWithInsertions: (
      state,
      action: PayloadAction<{ guids: string[]; insertions: Insertion[] }>,
    ) => omit(state, action.payload.guids),
    // note: this will actually unload all local component designs from every project/context.  Probably don't use it
    //   except in design client under assembly mode.
    allComponentDesignsUnloaded: (state) => omitBy(state, (model) => model.category === ModelCategory.ComponentDesign),
    projectSynchronized: (state, action: PayloadAction<{ [projectGuid: string]: ProjectSynchronizationPayload }>) => {
      const syncPayloads = Object.values(action.payload);

      // Remove all the old models first before adding new ones. Doing this project-by-project could result in models
      //   existing in two projects or being removed from both, which would be bad.

      const expiredModelGuids = new Set(syncPayloads.flatMap((payload) => payload.expiredModelGuids));
      for (const expiredGuid of expiredModelGuids) {
        delete state[expiredGuid];
      }

      const updatedModels = syncPayloads.flatMap((payload) => payload.incomingModels);
      for (const updatedModel of updatedModels) {
        state[updatedModel.guid] = updatedModel;
      }
    },
  },
});

export const {
  nonSynchronizedLayoutModelLoaded,
  projectArchived,
  componentDesignsBatchLocked,
  componentDesignsDeletedWithInsertions,
  allComponentDesignsUnloaded,
  projectSynchronized,
} = layoutModesSlice.actions;
export const layoutModels = layoutModesSlice.reducer;
